import { getResponseStatus } from "@istari-frontend/shared";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { entityIdFromPath, type EntityLink, type EntityLinkVerdict } from "~/ai-chat/model/entity-link";
import { DEFAULT_CACHE_TIME } from "~/constants";
import { jobsQueryOptions } from "~/jobs/data-source/jobs-query-options";
import { resourceExistsQueryOptions } from "~/resource/data-source/api/resource-exists-query-options";
import { systemQueryOptions } from "~/systems/data-source/system-query-options";

// Models and artifacts are both v3 resources, so they share the batched existence lookup.
// Systems and jobs have no batch endpoint; their chips reuse the detail queries (shared cache
// with the pages) and override only `select`, so the cached DTOs stay intact for other readers.
export function useEntityLinkVerdict(link: EntityLink): EntityLinkVerdict {
  const id = entityIdFromPath(link.path) ?? "";
  const isResource = link.type === "model" || link.type === "artifact";

  const resourceQuery = useQuery({
    ...resourceExistsQueryOptions(id),
    enabled: isResource && !!id,
  });
  const systemQuery = useQuery({
    ...systemQueryOptions(id),
    enabled: link.type === "system" && !!id,
    select: () => true,
  });
  const jobQuery = useQuery({
    ...jobsQueryOptions.byId(id, { enabled: link.type === "job" }),
    staleTime: DEFAULT_CACHE_TIME,
    select: () => true,
  });

  if (isResource) return toVerdict(resourceQuery, false);
  if (link.type === "system") return toVerdict(systemQuery, true);
  return toVerdict(jobQuery, true);
}

// A rejected batch lookup says nothing about one resource, so only the per-entity detail
// queries can turn an error into "missing".
function toVerdict(query: UseQueryResult<boolean>, statusIsPerEntity: boolean): EntityLinkVerdict {
  if (query.isSuccess) return query.data ? "found" : "missing";
  if (!query.isError) return "pending";
  const status = statusIsPerEntity ? getResponseStatus(query.error) : undefined;
  return status === 404 || status === 403 ? "missing" : "unverified";
}
